import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import Dialog from '../ui/Dialog';
import ActionBar from '../ui/ActionBar';
import Button from '../ui/Button';

/*
 * A metered account's AI credits (routes/ai_credits.py): this month's free
 * allowance, what is used and left, the purchased balance, when the free
 * credits come back, and the purchased balance's recent moves
 * (GET /ai-credits/me/ledger).
 *
 * "Buy AI credits" is there and says it is not yet: payments are phase 3 of
 * docs/PUBLIC_PORTAL_AND_ORDERING_PLAN.md. Until then a manager adds credits
 * from /users.
 */
export default function AiCreditsDialog({ credits, onClose }) {
  const { t, i18n } = useTranslation('credits');
  const [entries, setEntries] = useState(null);
  const [buyAsked, setBuyAsked] = useState(false);

  useEffect(() => {
    let live = true;
    Promise.resolve()
      .then(() => api.get('/ai-credits/me/ledger'))
      .then(res => { if (live) setEntries(res?.data?.entries || []); })
      .catch(() => { if (live) setEntries([]); });
    return () => { live = false; };
  }, []);

  const date = value => (value ? new Date(value).toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-GB',
    { day: 'numeric', month: 'long', year: 'numeric' }) : '');

  return (
    <Dialog title={t('dialog.title')} onClose={onClose} testId="ai-credits-dialog" width={460}>
      {!credits ? (
        <p style={muted}>{t('dialog.loading')}</p>
      ) : (
        <dl style={facts}>
          <dt>{t('dialog.allowance')}</dt>
          <dd data-testid="ai-credits-allowance">{credits.monthly_free_allowance}</dd>
          <dt>{t('dialog.used')}</dt>
          <dd data-testid="ai-credits-used">{credits.free_used}</dd>
          <dt>{t('dialog.freeLeft')}</dt>
          <dd data-testid="ai-credits-free-left">{credits.free_remaining}</dd>
          <dt>{t('dialog.purchased')}</dt>
          <dd data-testid="ai-credits-purchased">{credits.purchased_token_balance}</dd>
        </dl>
      )}
      {credits?.resets_at && (
        <p style={muted} data-testid="ai-credits-resets">{t('dialog.resets', { date: date(credits.resets_at) })}</p>
      )}
      <p style={muted}>{t('dialog.order')}</p>

      <h3 style={{ fontSize: 14, margin: '14px 0 6px' }}>{t('dialog.history')}</h3>
      {entries === null ? (
        <p style={muted}>{t('dialog.loading')}</p>
      ) : entries.length === 0 ? (
        <p style={muted} data-testid="ai-credits-no-history">{t('dialog.noHistory')}</p>
      ) : (
        <ul style={list} data-testid="ai-credits-history">
          {entries.map(e => (
            <li key={e.entry_id} style={row}>
              <span>{t(`kind.${e.kind}`)}{e.reason ? ` · ${e.reason}` : ''}</span>
              <span dir="ltr" style={{ fontWeight: 600 }}>{e.amount > 0 ? `+${e.amount}` : e.amount}</span>
              <span style={muted}>{date(e.created_at)}</span>
            </li>
          ))}
        </ul>
      )}

      {buyAsked && (
        <p role="status" style={{ ...muted, color: 'var(--c-text)' }} data-testid="ai-credits-buy-soon">
          {t('dialog.buySoon')}
        </p>
      )}
      <ActionBar>
        <Button onClick={onClose}>{t('dialog.close')}</Button>
        <Button variant="primary" onClick={() => setBuyAsked(true)} title={t('tip.buy')} data-testid="ai-credits-buy">
          {t('dialog.buy')}
        </Button>
      </ActionBar>
    </Dialog>
  );
}

const muted = { fontSize: 13, color: 'var(--c-text-muted-fg)', margin: '4px 0' };
const facts = { display: 'grid', gridTemplateColumns: 'auto auto', gap: '6px 16px', margin: '0 0 8px', fontSize: 14,
  justifyContent: 'start' };
const list = { listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6, fontSize: 13 };
const row = { display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10, alignItems: 'baseline' };
